import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOTH_IMAGE = "/booth-hero.jpg";

export default function FarmersMarketSection() {
  const [markets, setMarkets] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/markets`)
      .then((r) => setMarkets(r.data.markets || []))
      .catch(() => setMarkets([]));
  }, []);

  return (
    <section
      data-testid="farmers-market"
      className="relative bg-[color:var(--sr-bg)] py-24 md:py-40 overflow-hidden"
    >
      <div className="sr-container">
        <div className="grid grid-cols-12 gap-6 md:gap-12">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1 }}
            className="col-span-12 lg:col-span-6 relative"
          >
            <div className="relative aspect-[5/4] w-full overflow-hidden bg-[color:var(--sr-secondary)]">
              <motion.img
                src={BOOTH_IMAGE}
                alt="A Smiling Roots farmers market booth"
                className="w-full h-full object-cover"
                initial={{ scale: 1.08 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
              />
              <span className="absolute top-6 left-6 label-eyebrow text-[color:var(--sr-secondary)] bg-[color:var(--sr-primary)]/85 px-3 py-1.5">
                In the wild · Saturday mornings
              </span>
            </div>
          </motion.div>

          {/* Content */}
          <div className="col-span-12 lg:col-span-6 flex flex-col justify-center lg:pl-8">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7 }}
              className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3"
            >
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Meet us in person
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.9 }}
              className="font-serif text-5xl md:text-7xl leading-[0.92] tracking-tight text-[color:var(--sr-primary)]"
            >
              Visit us at your <br />
              <em className="italic font-light text-[color:var(--sr-accent)]">
                local farmers market.
              </em>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-8 max-w-md text-[color:var(--sr-text)]/75 leading-relaxed"
            >
              We pour, we pack, we shake hands. Find our booth beneath the
              linen canopy — samples poured cold, bottles ready to travel.
            </motion.p>

            <ul className="mt-10 divide-y divide-[color:var(--sr-border-light)] border-t hairline-light">
              {markets.map((m) => (
                <li
                  key={m.id}
                  className="py-5 flex items-start justify-between gap-6 group"
                  data-testid={`market-row-${m.id}`}
                >
                  <div>
                    <p className="font-serif text-2xl leading-tight text-[color:var(--sr-primary)] group-hover:text-[color:var(--sr-accent)] transition-colors">
                      {m.name}
                    </p>
                    <p className="text-sm text-[color:var(--sr-muted)] mt-1 flex items-center gap-2">
                      <MapPin size={13} strokeWidth={1.25} /> {m.city}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="label-eyebrow text-[color:var(--sr-primary)]">
                      {m.day}
                    </p>
                    <p className="text-xs text-[color:var(--sr-muted)] mt-1 tabular-nums">
                      {m.hours}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap items-center gap-5">
              <Link
                to="/markets"
                data-testid="markets-cta"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors"
              >
                <span>Full schedule & pre-order</span>
                <ArrowUpRight size={16} strokeWidth={1.25} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
